import React, { useEffect, useState } from 'react';
import { marked } from 'marked';
import PropTypes from 'prop-types';
import VuiBox from 'components/ui/VuiBox';

// Domain → color tokens. Used both in JS (tag generation) and CSS (class selectors).
const DOMAIN_COLORS = {
  CLINICAL: { color: '#34d399', bg: 'rgba(52, 211, 153, 0.12)', border: 'rgba(52, 211, 153, 0.3)' },  // teal
  NEWS:     { color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.12)',  border: 'rgba(251, 191, 36, 0.3)'  },  // amber
  HIRING:   { color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.12)',  border: 'rgba(96, 165, 250, 0.3)'  },  // blue
  SOCIAL:   { color: '#a78bfa', bg: 'rgba(167, 139, 250, 0.12)', border: 'rgba(167, 139, 250, 0.25)' }, // purple
  SIGNAL:   { color: '#9ca3af', bg: 'rgba(156, 163, 175, 0.12)', border: 'rgba(156, 163, 175, 0.25)' }, // grey
};

const tagStyles = Object.fromEntries(
  Object.entries(DOMAIN_COLORS).map(([domain, { color, bg, border }]) => [
    `& .signal-tag-${domain}`, {
      display: 'inline-block',
      fontWeight: '600',
      fontSize: '0.8rem',
      color,
      background: bg,
      border: `1px solid ${border}`,
      borderRadius: '4px',
      padding: '1px 7px',
      marginRight: '6px',
      letterSpacing: '0.02em',
      textIndent: 0,
      verticalAlign: 'middle',
      lineHeight: '1.6',
    }
  ])
);

const styles = {
  color: 'rgba(255, 255, 255, 0.8)',
  fontSize: '1rem',
  lineHeight: 1.7,
  '& ul': {
    listStyleType: 'none',
    paddingLeft: '20px',
    margin: 0,
  },
  '& li': {
    marginBottom: '10px',
    paddingLeft: '1em',
    textIndent: '-1.2em',
    '&::before': {
      content: '"•"',
      marginRight: '0.5em',
      color: 'rgba(255,255,255,0.3)',
      fontSize: '1.2em',
      lineHeight: 0,
    },
  },
  '& ul ul': {
    marginTop: '8px',
    paddingLeft: '25px',
  },
  '& li li': {
    fontSize: '0.95rem',
    textIndent: '-1.1em',
    '&::before': {
      content: '"–"',
      fontWeight: 'bold',
    },
  },
  '& p': {
    margin: '0 0 6px 0',
  },
  '& p:has(> strong:only-child)': {
    marginTop: '16px',
    marginBottom: '6px',
    fontSize: '0.82rem',
    fontWeight: 700,
    letterSpacing: '0.03em',
  },
  '& p:first-of-type:has(> strong:only-child)': {
    marginTop: 0,
  },
  '& strong': {
    fontWeight: 'bold',
    color: 'rgba(255, 255, 255, 0.95)',
  },
  ...tagStyles,
};

// Known domain keywords — controls tag color class
const KNOWN_DOMAINS = new Set(Object.keys(DOMAIN_COLORS));

function domainClass(domain) {
  const d = domain.toUpperCase();
  return KNOWN_DOMAINS.has(d) ? `signal-tag-${d}` : 'signal-tag-SIGNAL';
}

// [COMPANY — DOMAIN], [COMPANY - DOMAIN], [COMPANY -- DOMAIN] — extract domain from suffix
const tagWithCompanyPattern = /\[([^\]—–\-]+?)\s*(?:—|–|--|-)\s*([A-Z]+)\]/g;
// Bare [DOMAIN] tags
const tagBarePattern = /\[(CLINICAL|NEWS|HIRING|SOCIAL|SIGNAL)\]/g;

function applySignalTags(html, stripCompany = false) {
  return html
    .replace(tagWithCompanyPattern, (_, company, domain) => {
      const label = stripCompany ? domain : `${company.trim()} — ${domain}`;
      return `<span class="${domainClass(domain)}">${label}</span>&nbsp;`;
    })
    .replace(tagBarePattern, (_, domain) =>
      `<span class="${domainClass(domain)}">${domain}</span>&nbsp;`
    );
}

// ── Recommendations deduplication ────────────────────────────────────────────
// If the LLM outputs repeated "For Marketing:" / "For Sales:" top-level bullets
// instead of grouped sub-bullets, merge them into one grouped structure.
const REC_PATTERN = /^(\s*[-*]?\s*)\*\*(For (?:Marketing|Sales)):\*\*\s*/;

function mergeRecommendations(markdown) {
  if (!markdown) return markdown;
  const lines = markdown.split('\n');
  const groups = {}; // label → [content lines]
  const order = [];
  let currentLabel = null;

  for (const line of lines) {
    const match = line.match(REC_PATTERN);
    if (match) {
      const label = match[2]; // "For Marketing" or "For Sales"
      if (!groups[label]) {
        groups[label] = [];
        order.push(label);
      }
      const content = line.replace(REC_PATTERN, '').trim();
      if (content) groups[label].push(`  - ${content}`);
      currentLabel = label;
    } else if (currentLabel && line.trim().startsWith('-') && !line.match(/^\s*-\s*\*\*/)) {
      // continuation sub-bullet under current label
      groups[currentLabel].push(`  ${line.trim()}`);
    } else {
      currentLabel = null;
    }
  }

  // Only rewrite if we actually found repeated labels (more than 1 bullet per label)
  const hasRepeats = order.some(label => groups[label].length > 1 ||
    order.filter(l => l === label).length > 1);

  if (!hasRepeats || order.length === 0) return markdown;

  return order
    .filter((label, i) => order.indexOf(label) === i) // dedupe order
    .map(label => `- **${label}:**\n${groups[label].join('\n')}`)
    .join('\n');
}

const MarkdownRenderer = ({ markdownText, stripCompany = false }) => {
  const [html, setHtml] = useState(() => {
    if (!markdownText) return '';
    const processed = mergeRecommendations(markdownText);
    const rawMarkup = marked(processed, { breaks: true, gfm: true });
    return applySignalTags(rawMarkup, stripCompany);
  });

  useEffect(() => {
    if (markdownText) {
      const processed = mergeRecommendations(markdownText);
      const rawMarkup = marked(processed, { breaks: true, gfm: true });
      setHtml(applySignalTags(rawMarkup, stripCompany));
    }
  }, [markdownText, stripCompany]);

  return (
    <VuiBox
      sx={styles}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

MarkdownRenderer.propTypes = {
  markdownText: PropTypes.string.isRequired,
  stripCompany: PropTypes.bool,
};

export default MarkdownRenderer;
